import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { ClickableEmptyChatInstructionsOption } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import type { UserMessageOptions } from "@mirage/service-conversation/types";
import type { AssistCustomTemplate } from "@mirage/shared/compose/assist-template";
import type { ComposeVoice } from "@mirage/shared/compose/compose-voice";
import React from "react";
import {
  AIToolLibraryModal,
  type AIToolLibraryTab,
} from "../components/AIToolLibraryModal";
import { useAIToolLibraryTemplates } from "./useAIToolLibraryTemplates";
import { useAIToolLibraryVoices } from "./useAIToolLibraryVoices";

export interface AIToolLibraryConfig {
  tab?: AIToolLibraryTab;
  templateID?: string;
  voiceID?: string;
}

interface UseAIToolLibraryProps {
  onTemplateChange: (templateId: string | null, templateName: string) => void;
  onVoiceChange: (voiceId: string, voiceName: string) => void;
  onPostMessageWithVoice: (voiceId: string) => void;
  onPostMessageWithTemplate: (templateId: string) => void;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  setUserMessageOptions: (options: UserMessageOptions) => void;
  setEmptyChatOption: (option: ClickableEmptyChatInstructionsOption) => void;
  currentTemplateID?: string | null;
  currentVoiceID?: string | null;
  markdownDraft: string;
  isLibraryOpen: boolean;
  activeTab: AIToolLibraryTab;
  openLibrary: (config?: AIToolLibraryConfig) => void;
  closeLibrary: () => void;
  switchTab: (tab: AIToolLibraryTab) => void;
}

interface UseAIToolLibraryReturn {
  aiToolLibraryModal: React.ReactNode;
  templates: AssistCustomTemplate[];
  selectedTemplateId?: string;
  handleTemplateChange: (
    templateId: string | null,
    templateName: string,
  ) => void;
  saveTemplate: (
    template: AssistCustomTemplate,
    onSaved?: (savedTemplate: AssistCustomTemplate) => void,
  ) => Promise<void>;
  deleteTemplate: (templateId: string) => void;
  voices: ComposeVoice[];
  selectedVoiceId?: string | null;
  handleVoiceChange: (voiceId: string, voiceName: string) => void;
  onPostMessageWithVoice: (voiceId: string) => void;
  saveVoice: (voice: ComposeVoice) => Promise<void | string>;
  deleteVoice: (voiceId: string) => Promise<void>;
  setVoiceSnackbarMsg: (
    msg: { message: string; type: "success" | "error" } | undefined,
  ) => void;
  voiceSettingsSnackbar: React.ReactNode;
}

export const useAIToolLibrary = ({
  onTemplateChange,
  onVoiceChange,
  onPostMessageWithVoice,
  onPostMessageWithTemplate,
  setUserMessageOptions,
  setEmptyChatOption,
  logComposeEvent,
  currentTemplateID,
  currentVoiceID,
  markdownDraft,
  isLibraryOpen,
  activeTab,
  closeLibrary,
  switchTab,
}: UseAIToolLibraryProps): UseAIToolLibraryReturn => {
  const {
    templates,
    selectedTemplateId,
    handleTemplateChange,
    handleUseTemplate,
    saveTemplate,
    deleteTemplate,
    templateSettingsSnackbar,
    handleSetCreatedTemplateSnackbarMsg,
  } = useAIToolLibraryTemplates({
    onTemplateChange,
    setUserMessageOptions,
    setEmptyChatOption,
    onCloseLibrary: closeLibrary,
    currentTemplateID,
    onPostMessageWithTemplate,
    markdownDraft,
  });

  const {
    voices,
    selectedVoiceId,
    handleVoiceChange,
    handleUseVoice,
    saveVoice,
    deleteVoice,
    setVoiceSnackbarMsg,
    voiceSettingsSnackbar,
    handleSetCreatedVoiceSnackbarMsg,
  } = useAIToolLibraryVoices({
    onVoiceChange,
    setUserMessageOptions,
    setEmptyChatOption,
    onCloseLibrary: closeLibrary,
    currentVoiceID,
    onPostMessageWithVoice,
    markdownDraft,
  });

  const aiToolLibraryModal = (
    <AIToolLibraryModal
      isOpen={isLibraryOpen}
      activeTab={activeTab}
      onRequestClose={closeLibrary}
      onSwitchTab={switchTab}
      templates={templates}
      onUseTemplate={handleUseTemplate}
      currentTemplateID={selectedTemplateId}
      saveTemplate={saveTemplate}
      onDeleteTemplate={deleteTemplate}
      setCreatedTemplateSnackbarMsg={handleSetCreatedTemplateSnackbarMsg}
      templateSettingsSnackbar={templateSettingsSnackbar}
      voices={voices}
      onUseVoice={handleUseVoice}
      currentVoiceID={selectedVoiceId}
      saveVoice={saveVoice}
      onDeleteVoice={deleteVoice}
      setCreatedVoiceSnackbarMsg={handleSetCreatedVoiceSnackbarMsg}
      voiceSettingsSnackbar={voiceSettingsSnackbar}
      logComposeEvent={logComposeEvent}
    />
  );

  return {
    aiToolLibraryModal,
    templates,
    selectedTemplateId,
    handleTemplateChange,
    saveTemplate,
    deleteTemplate,
    voices,
    selectedVoiceId,
    handleVoiceChange,
    onPostMessageWithVoice,
    saveVoice,
    deleteVoice,
    setVoiceSnackbarMsg,
    voiceSettingsSnackbar,
  };
};
